import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { cartPageModuleQuery } from '@data/sanity/queries/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityCartPage } from '@data/sanity/queries/types/page'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPageSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getCartPage } from '@lib/sanity/page'
import { Locale } from '@lib/language'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface CartProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  page: SanityCartPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const Cart = ({
  draftMode,
  draftToken,
  locale,
  page,
  site,
  reviews,
}: CartProps) => {
  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={cartPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
            }}
            reviews={reviews}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} reviews={reviews} />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<CartProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode,
  )
  const cartPage = await getCartPage(sanityClient, context.locale)

  if (!cartPage.page) {
    return {
      notFound: true,
    }
  }

  // Get Stamped.io reviews
  let reviews: Reviews = {
    rating: 0,
    items: [],
  }

  try {
    const shopifyDomain = getShopifyDomain(context.locale)
    reviews = await getReviews(
      cartPage.page.modules ?? [],
      shopifyDomain,
      cartPage.site.settings.stampedApiKey,
    )
  } catch (error) {
    console.error(error)
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      page: cartPage.page,
      site: cartPage.site,
      reviews,
    },
    revalidate: 60,
  }
}

export default Cart
